/**
 * Primitives every contract in this package is built from: field-level schemas,
 * the error envelope every non-2xx response uses, and pagination helpers.
 *
 * Never re-declare these shapes locally in an app — import them.
 */
import { z } from 'zod';

/* -------------------------------------------------------------------------- */
/* Field primitives                                                            */
/* -------------------------------------------------------------------------- */

export const uuidSchema = z.string().uuid();
export type Uuid = z.infer<typeof uuidSchema>;

/**
 * Emails are normalised (trimmed + lower-cased) on the way in. `users.email` is
 * globally unique in the database and the column carries a `= lower(email)`
 * CHECK, so every write path must feed it through this schema.
 */
export const emailSchema = z.string().trim().toLowerCase().email().max(254);

/** E.164, e.g. `+919812345678`. */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+[1-9]\d{6,14}$/, 'Phone must be in E.164 format, e.g. +919812345678');

/**
 * Date-times cross the wire as ISO-8601 strings. Accepts a `Date` on the way in
 * so a service can hand a response schema its raw row without converting first.
 */
export const isoDateTimeSchema = z
  .union([z.string().datetime({ offset: true }), z.date()])
  .transform((value) => (typeof value === 'string' ? value : value.toISOString()));
export type IsoDateTime = z.infer<typeof isoDateTimeSchema>;

export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 256;

/**
 * Password policy, used only where a password is *set*. Login deliberately does
 * not apply it, so a policy change can never lock an existing user out and the
 * error can never hint at the stored value.
 *
 * SECURITY: the messages below never echo the submitted value, and no error
 * produced from this schema may be logged with its input.
 */
export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Password must be at least ${MIN_PASSWORD_LENGTH} characters`)
  .max(MAX_PASSWORD_LENGTH)
  .refine((value) => /[a-z]/.test(value), 'Password must contain a lower case letter')
  .refine((value) => /[A-Z]/.test(value), 'Password must contain an upper case letter')
  .refine((value) => /\d/.test(value), 'Password must contain a number');

export const SORT_DIRECTION_VALUES = ['asc', 'desc'] as const;
export const sortDirectionSchema = z.enum(SORT_DIRECTION_VALUES);
export type SortDirection = z.infer<typeof sortDirectionSchema>;

/* -------------------------------------------------------------------------- */
/* Error envelope                                                              */
/* -------------------------------------------------------------------------- */

export const ERROR_CODE_VALUES = [
  'bad_request',
  'validation_failed',
  'unauthorized',
  'forbidden',
  'not_found',
  'conflict',
  'rate_limited',
  'internal',
] as const;
export const errorCodeSchema = z.enum(ERROR_CODE_VALUES);
export type ErrorCode = z.infer<typeof errorCodeSchema>;

export const fieldErrorSchema = z.object({
  /** Dot/bracket path into the request body, e.g. `owner.email`. */
  path: z.string(),
  message: z.string(),
});
export type FieldError = z.infer<typeof fieldErrorSchema>;

/**
 * The single response shape for every failure. Nothing sensitive goes in here:
 * no password, no token value, no password hash, no raw provider payload.
 */
export const errorEnvelopeSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    fieldErrors: z.array(fieldErrorSchema).optional(),
    /** Correlates the response with a log line. */
    requestId: z.string().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof errorEnvelopeSchema>;

export function errorEnvelope(
  code: ErrorCode,
  message: string,
  extra?: { fieldErrors?: FieldError[]; requestId?: string },
): ErrorEnvelope {
  return {
    error: {
      code,
      message,
      ...(extra?.fieldErrors ? { fieldErrors: extra.fieldErrors } : {}),
      ...(extra?.requestId ? { requestId: extra.requestId } : {}),
    },
  };
}

/** Turns a zod failure into the wire error envelope. */
export function fieldErrorsFromZod(error: z.ZodError): FieldError[] {
  return error.issues.map((issue) => ({
    path: issue.path.join('.'),
    message: issue.message,
  }));
}

/** Response body for endpoints whose only meaningful answer is "it worked". */
export const okResponseSchema = z.object({ success: z.literal(true) });
export type OkResponse = z.infer<typeof okResponseSchema>;

export const OK_RESPONSE: OkResponse = { success: true };

/* -------------------------------------------------------------------------- */
/* Pagination                                                                  */
/* -------------------------------------------------------------------------- */

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;

/** Query string shape for every list endpoint. Values arrive as strings. */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
export type PaginationQuery = z.infer<typeof paginationQuerySchema>;

export const paginationMetaSchema = z.object({
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  total: z.number().int().min(0),
  totalPages: z.number().int().min(0),
});
export type PaginationMeta = z.infer<typeof paginationMetaSchema>;

/** Wraps an item schema in the standard `{ items, pagination }` envelope. */
export function paginated<TItem extends z.ZodTypeAny>(item: TItem) {
  return z.object({
    items: z.array(item),
    pagination: paginationMetaSchema,
  });
}

export type Paginated<TItem> = {
  items: TItem[];
  pagination: PaginationMeta;
};

export function paginationMeta(query: PaginationQuery, total: number): PaginationMeta {
  return {
    page: query.page,
    pageSize: query.pageSize,
    total,
    totalPages: Math.ceil(total / query.pageSize),
  };
}

/** SQL OFFSET for a page query. */
export function paginationOffset(query: PaginationQuery): number {
  return (query.page - 1) * query.pageSize;
}
